/**
 * What a component inside a chat reply may DO when it is tapped, beyond
 * opening a page or answering in words.
 *
 * Two things, and only two:
 *
 *   · `decideFromChat` — decide an approval card that already exists. The card
 *     IS the approval: it was staged by the ordinary tool path, with its
 *     checks, and it says what will happen, to whom and in what words. A
 *     component never sends anything of its own.
 *   · `saveFromChat` — a direct write to the app's own API for something that
 *     reaches nobody (ticking a part as picked up, adding receipt lines to a
 *     job's costs, attaching photos to an unsent report). The route does its
 *     own permission check; this only carries the request.
 *
 * Both resolve with the SERVER'S sentence on a refusal (#35), for the
 * component to show inline beside the tap (#42) — never a toast, never a
 * generic "something went wrong" when the route said what.
 *
 * Plain fetch, no React and no directive: the panel and the Mucka Bar draw the
 * same components from the same file, and both call these directly, so a tap
 * on the phone and a tap on the desktop are the same request.
 */
import { parseApprovalResponse } from '@/lib/engine/approval-response';

export interface ChatTapResult {
  ok: boolean;
  /** The server's own words when it refused, else null. */
  message: string | null;
  /** The route's JSON body on success, for a component that needs the result. */
  data?: unknown;
}

export type ChatCardDecision = 'approved' | 'snoozed' | 'dismissed';

/** Said when the network itself failed, so there is no server sentence to show. */
export const CHAT_TAP_OFFLINE = "That didn't go through, so nothing has changed. Check your connection and try again.";

/**
 * Event the panel listens for so its own copy of a card (the staged strip in
 * the same reply) moves with a decision made from a component.
 */
export const CHAT_CARD_DECIDED_EVENT = 'mucka:chat-card-decided';

export async function decideFromChat(actionId: string, decision: ChatCardDecision): Promise<ChatTapResult> {
  try {
    const res = await fetch(`/api/actions/${encodeURIComponent(actionId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: decision }),
    });
    const body = await res.json().catch(() => ({}));
    if (decision === 'approved') {
      const { failed, message } = parseApprovalResponse(res.ok, body);
      if (failed) return { ok: false, message };
    } else if (!res.ok) {
      return { ok: false, message: readError(body) };
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CHAT_CARD_DECIDED_EVENT, { detail: { id: actionId, decision } }));
    }
    return { ok: true, message: null, data: body };
  } catch {
    return { ok: false, message: CHAT_TAP_OFFLINE };
  }
}

export async function saveFromChat(
  method: 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<ChatTapResult> {
  if (!path.startsWith('/api/')) throw new Error(`saveFromChat only writes to the app's own API, not ${path}`);
  try {
    const res = await fetch(path, {
      method,
      headers: { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: readError(data) };
    return { ok: true, message: null, data };
  } catch {
    return { ok: false, message: CHAT_TAP_OFFLINE };
  }
}

function readError(body: unknown): string {
  const e = (body as { error?: unknown } | null)?.error;
  return typeof e === 'string' && e.trim() ? e : "That didn't work, so nothing has changed. Give it another go in a minute.";
}
